import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Twitter, Instagram, Linkedin, Mail, Phone, MessageCircle, MapPin, Send } from "lucide-react";
import emailjs from '@emailjs/browser';

const Contact = () => {
  // State for FAQ dropdowns
  const [openFAQIndex, setOpenFAQIndex] = useState(null);
  // State for form inputs
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });

  const [status, setStatus] = useState('');

  const toggleFAQ = (index) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // EmailJS credentials
    const serviceId = 'service_led2lead';
    const templateId = 'template_5suke1f';
    const publicKey = '-_LciainSUwEQQnuD';

    // emailjs.send(serviceId, templateId, {
    //   from_name: formData.name,
    //   email: formData.email,
    //   subject: formData.subject,
    //   message: formData.message
    // }, publicKey)
    emailjs.send(serviceId, templateId, formData, publicKey)
      .then((response) => {
        setStatus('Message sent successfully!');
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
        setTimeout(() => {
          setStatus('');
        }, 1000);
      })
      .catch((error) => {
        setStatus('Failed to send message. Please try again.');
        console.error('Email send error:', error);
      });

  };

  const faqs = [
    {
      question: "How can I get in touch with you?",
      answer: "You can use the contact form on this page or reach out to us via our social media channels. For urgent matters, please call our customer service line directly.",
    },
    {
      question: "What are your operating hours?",
      answer: "Our support team is available Monday to Friday, from 9 AM to 5 PM. For after-hours support, please leave a message and we'll get back to you on the next business day.",
    },
    {
      question: "Do you offer consultation services?",
      answer: "Yes, we offer personalized consultation services tailored to your specific needs. Please get in touch via the contact form for more details and to schedule your consultation session.",
    },
    {
      question: "How soon can I expect a response?",
      answer: "We strive to respond to all inquiries within 24 hours during business days. For complex matters, it might take up to 48 hours to provide you with a comprehensive response.",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-amber-50">
      <div className="container mx-auto px-4 sm:px-6 py-16">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-green-600 to-green-800 text-white pt-24 pb-12 sm:py-28 rounded-xl text-center relative overflow-hidden shadow-lg min-h-[60vh] flex flex-col justify-center"
        >
          {/* Decorative elements */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400 opacity-20 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-400 opacity-20 rounded-full -ml-12 -mb-12"></div>
          </div>

          <div className="relative z-10 px-6">
            <h1 className="text-4xl md:text-5xl font-bold">
              Get in <span className="text-amber-300">Touch</span>
            </h1>
            <p className="mt-4 text-xl md:text-2xl max-w-2xl mx-auto">
              We'd love to hear from you. Reach out for inquiries, support, or just to say hello!
            </p>
          </div>
        </motion.section>


        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {/* Contact Information Card */}
          <motion.section
            variants={itemVariants}
            className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-amber-400 h-full"
          >
            <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
              <MapPin className="mr-2 text-amber-500" />
              Find Us
            </h2>

            <div className="space-y-6">
              {/* Email Section */}
              <div className="flex items-center space-x-4 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <div className="bg-amber-100 p-2 rounded-full">
                  <Mail className="text-green-600 w-5 h-5" />
                </div>
                <div className="w-full min-w-0">
  <p className="text-sm text-gray-500">Email</p>
  <p className="font-medium text-xs break-words">bookled2lead@gmail.com</p> 
  </div>
              </div>

              {/* Phone Section */}
              <div className="flex items-center space-x-4 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <div className="bg-amber-100 p-2 rounded-full">
                  <Phone className="text-green-600 w-5 h-5" />
                </div>
                <div className="w-full min-w-0">
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">09157064112</p>
                </div>
              </div>

              {/* WhatsApp Section */}
              <div className="flex items-center space-x-4 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <div className="bg-amber-100 p-2 rounded-full">
                  <MessageCircle className="text-green-600 w-5 h-5" />
                </div>
                <div className="w-full min-w-0">
                  <p className="text-sm text-gray-500">WhatsApp</p>
                  <p className="font-medium">09166324931</p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="font-semibold text-green-700 mb-4">Connect With Us</h3>
              <div className="flex space-x-4">
                <a href="https://x.com/led2_lead?s=21&t=BgNN3lDwWva3ddKrhGunJg" className="bg-green-100 p-2 rounded-full text-green-600 hover:bg-amber-100 hover:text-green-800 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://www.instagram.com/led2_lead/" className="bg-green-100 p-2 rounded-full text-green-600 hover:bg-amber-100 hover:text-green-800 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.linkedin.com/company/led2leadconsultinglimited/" className="bg-green-100 p-2 rounded-full text-green-600 hover:bg-amber-100 hover:text-green-800 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.section>


          {/* Contact Form Section */}
          <motion.section
            variants={itemVariants}
            className="bg-white rounded-xl shadow-lg p-8 lg:col-span-2 border-t-4 border-green-600"
          >
            <h2 className="text-2xl font-bold text-green-700 mb-6 flex items-center">
              <Send className="mr-2 text-amber-500" />
              Send Us a Message
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent bg-gray-50"
                    placeholder="Your Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent bg-gray-50"
                    placeholder="Your Email"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-1">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent bg-gray-50"
                  placeholder="Subject"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-1">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent bg-gray-50"
                  rows="5"
                  placeholder="Your Message"
                  required
                ></textarea>
              </div>

              {status && (
                <div className={`mt-4 p-3 rounded text-center ${status.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {status}
                </div>
              )}

              <div className="text-center">
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition shadow-md relative overflow-hidden group"
                >
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-amber-400 to-amber-300 opacity-0 group-hover:opacity-20 transition-opacity"></span>
                  <span className="relative flex items-center justify-center">
                    Send Message
                    <Send className="ml-2 w-4 h-4" />
                  </span>
                </motion.button>
              </div>
            </form>
          </motion.section>
        </motion.div>

        {/* FAQs Section */}
        <motion.section
          variants={itemVariants}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-12 bg-white p-8 rounded-xl shadow-lg border-l-4 border-amber-400"
        >
          <h2 className="text-2xl font-bold text-green-700 text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * index }}
                className="border border-gray-100 rounded-lg overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-4 text-left text-lg font-medium flex justify-between items-center bg-gray-50 hover:bg-amber-50 transition-colors"
                >
                  <span>{faq.question}</span>
                  {openFAQIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-green-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-green-600" />
                  )}
                </button>

                {openFAQIndex === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.3 }}
                    className="p-4 bg-white border-t border-gray-100 text-gray-600"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Contact;